import type { Dictionary } from "@/lib/i18n";
import { getDictionary, localePath, sectionPagePath, type Locale, type SectionKey } from "@/lib/i18n";
import { getSiteUrl } from "@/lib/site";

const ORG_ID = "#organization";
const SITE_ID = "#website";
const SOFTWARE_ID = "#software";

function softwareProductName(d: Dictionary): string {
  // Varlık adı her zaman marka adı ("oravity" → "Oravity"); SEO başlığının
  // biçiminden (— / | vb.) bağımsız kalsın.
  const raw = d.brand.name;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Kök düzen: Organization + WebSite + SoftwareApplication (aynı graf). */
export function buildGlobalJsonLdGraph(locale: Locale): Record<string, unknown>[] {
  const base = getSiteUrl().replace(/\/$/, "");
  const d = getDictionary(locale);
  const orgUrl = `${base}${ORG_ID}`;
  const siteUrl = `${base}${SITE_ID}`;
  const softwareUrl = `${base}${SOFTWARE_ID}`;
  const inLang = locale === "tr" ? "tr-TR" : "en-US";

  const organization: Record<string, unknown> = {
    "@type": "Organization",
    "@id": orgUrl,
    name: softwareProductName(d),
    alternateName: d.brand.name,
    url: base,
    logo: `${base}/brand/oravity-mark.png`,
    description: d.meta.description,
    inLanguage: inLang,
    email: d.contact.email,
    // Varlık sinyali: Google ve AI motorları markayı tanısın. Yalnız sitede yayında
    // olan bilgiler kullanıldı — sosyal hesap (sameAs) yok, uydurulmadı.
    areaServed: { "@type": "Country", name: "Türkiye" },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: d.contact.email,
        url: `${base}${sectionPagePath(locale, "contact")}`,
        availableLanguage: ["Turkish", "English"],
      },
    ],
  };

  const website: Record<string, unknown> = {
    "@type": "WebSite",
    "@id": siteUrl,
    name: softwareProductName(d),
    url: `${base}/`,
    inLanguage: inLang,
    publisher: { "@id": orgUrl },
  };

  const software: Record<string, unknown> = {
    "@type": ["SoftwareApplication", "WebApplication"],
    "@id": softwareUrl,
    name: softwareProductName(d),
    description: d.seo.softwareDescription,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web browser",
    featureList: d.seo.moduleHighlights,
    url: `${base}/`,
    manufacturer: { "@id": orgUrl },
    isPartOf: { "@id": siteUrl },
  };

  return [organization, website, software];
}

/** Alt sayfa: Ana sayfa → bölüm. */
export function buildBreadcrumbJsonLd(locale: Locale, sectionKey: SectionKey): Record<string, unknown> {
  const base = getSiteUrl().replace(/\/$/, "");
  const d = getDictionary(locale);
  const homeSeg = localePath(locale);
  const homeUrl = homeSeg === "/" ? `${base}/` : `${base}${homeSeg}`;
  const pagePath = sectionPagePath(locale, sectionKey);
  const pageUrl = `${base}${pagePath}`;

  const sectionTitles: Record<SectionKey, string> = {
    about: d.about.title,
    features: d.features.title,
    references: d.references.title,
    team: d.team.title,
    contact: d.contact.title,
  };

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: d.nav.home,
        item: homeUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: sectionTitles[sectionKey],
        item: pageUrl,
      },
    ],
  };
}
